import * as React from "react";

import type { SpendReportBase } from "@/types/spend";
import type { ReportViewProps } from "../types";
import { limitRows } from "../report-utils";
import { ReportCard } from "./report-card";
import { ReportControls } from "./report-controls";
import { ReportNotes } from "./report-notes";
import { ReportTable, type ReportColumn } from "./report-table";
import { ReportTrendChart } from "./report-trend-chart";

interface Props<Row> extends ReportViewProps {
  data: SpendReportBase;
  isFetching: boolean;
  /** Rows of the selected month that match the search, before Top N. */
  rows: Row[];
  searchLabel: string;
  extraControls?: React.ReactNode;
  notes: React.ReactNode;
  cards: React.ComponentProps<typeof ReportCard>[];
  chart: { title: string; subtitle: string; idLabel: string };
  table: {
    label: string;
    columns: ReportColumn<Row>[];
    rowKey: (row: Row) => string;
    totalLabel: string;
    emptyTitle: string;
  };
  onExport: (rows: Row[]) => void;
}

/** Controls, notes, summary cards, trend and table shared by every Spend Centre report. */
export const ReportLayout = <Row,>({
  state,
  onStateChange,
  data,
  isFetching,
  rows,
  searchLabel,
  extraControls,
  notes,
  cards,
  chart,
  table,
  onExport,
}: Props<Row>) => {
  const shown = limitRows(rows, state.top);
  return (
    <div className={isFetching ? "space-y-5 opacity-70" : "space-y-5"}>
      <ReportControls
        month={data.month}
        months={data.available_months}
        onMonthChange={month => onStateChange({ month })}
        top={state.top}
        onTopChange={top => onStateChange({ top })}
        search={state.search}
        onSearchChange={search => onStateChange({ search })}
        searchLabel={searchLabel}
        onExport={() => onExport(shown)}
        exportDisabled={!shown.length}
      >
        {extraControls}
      </ReportControls>
      <ReportNotes
        month={data.month}
        partialMonth={data.partial_month}
        lastInvoiceDate={data.last_invoice_date}
        droppedMissingRateCount={data.dropped_missing_rate_count}
      >
        {notes}
      </ReportNotes>
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Report summary">
        {cards.map(card => (
          <ReportCard key={card.title} {...card} />
        ))}
      </section>
      <ReportTrendChart
        title={chart.title}
        subtitle={chart.subtitle}
        trend={data.trend}
        selectedMonth={data.month}
        idLabel={chart.idLabel}
      />
      {shown.length ? (
        <ReportTable
          label={table.label}
          columns={table.columns}
          rows={shown}
          rowKey={table.rowKey}
          totalLabel={table.totalLabel}
        />
      ) : (
        <div className="rounded-lg border border-spend-border bg-white p-12 text-center">
          <p className="font-semibold text-spend-ink">{table.emptyTitle}</p>
          <p className="mt-1 text-sm text-gray-500">Choose another month or clear the search.</p>
        </div>
      )}
    </div>
  );
};
